import { useLayoutEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { getScrollPosition } from "@/lib/scrollPositions";

/**
 * Wraps page content with:
 *  - A brief fade-in on every route change (masks any scroll-restore flicker).
 *  - A scroll-to-top on mount of any route that has no saved scroll position
 *    (and isn't an explicit fresh navigation, which is handled elsewhere).
 *
 * Keyed by pathname so the wrapper remounts on every navigation, retriggering
 * both the CSS animation and the layout effect synchronously before paint.
 */
function PageTransitionInner({ children }: { children: ReactNode }) {
  const { pathname, state } = useLocation();

  useLayoutEffect(() => {
    const isFresh = (state as any)?.fresh === true;
    if (isFresh) {
      window.scrollTo(0, 0);
      return;
    }
    // If this route has no saved scroll position, start at the top.
    // Routes that do (e.g. /annonser on back-nav) handle their own restore.
    if (getScrollPosition(pathname) == null) {
      window.scrollTo(0, 0);
    }
    // run once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="page-fade">{children}</div>;
}

export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <PageTransitionInner key={pathname}>{children}</PageTransitionInner>;
}
